import React, { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  url: string;
  type?: 'website' | 'article';
  image?: string;
  jsonLd?: object;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  url,
  type = 'website',
  image = 'https://parismin.com/og-image.jpg',
  jsonLd,
}) => {
  const fullUrl = `https://parismin.com${url}`;
  const defaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebSite',
    name: title,
    description: description,
    url: fullUrl,
    image: image,
    publisher: {
      '@type': 'Organization',
      name: 'Parismin',
      url: 'https://parismin.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://parismin.com/logo.png',
      },
    },
  };

  const finalJsonLd = jsonLd || defaultJsonLd;

  useEffect(() => {
    // Update document title
    document.title = `${title} | Parismin`;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    // Update JSON-LD
    let jsonLdScript = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(finalJsonLd);
  }, [title, description, url, type, image, fullUrl, finalJsonLd]);

  return null;
};

export default SEO;

